/**
 * InputFrame: one player's logical buttons for one tick, packed in a byte (ARCHITECTURE §5 "Controls").
 *
 *   bit 0  L  (◀)          bit 3  D  (▼ down / cancel / fast-fall / truck)
 *   bit 1  R  (▶)          bit 4  A  (Action: snap / throw / grab / stiff-arm)
 *   bit 2  U  (▲ jump)     bit 5  S  (Special)
 *   bits 6–7 reserved (must be 0; masked off by `sanitizeInput`)
 *
 * The sim derives presses/releases, double-taps and hold durations itself from the input history in SimState,
 * so the client only ever sends raw "is held this tick" bits. Input is sampled once per sim tick.
 */

export type InputFrame = number;

export const Btn = {
  L: 1 << 0,
  R: 1 << 1,
  U: 1 << 2,
  D: 1 << 3,
  A: 1 << 4,
  S: 1 << 5,
} as const;
export type Btn = (typeof Btn)[keyof typeof Btn];

export const INPUT_NONE: InputFrame = 0;
/** All defined bits. */
export const INPUT_MASK = 0b0011_1111;
export const BUTTON_NAMES = ['L', 'R', 'U', 'D', 'A', 'S'] as const;
export type ButtonName = (typeof BUTTON_NAMES)[number];

/** Keep only defined bits; reject non-integers. Apply at every trust boundary (network, devices). */
export const sanitizeInput = (f: number): InputFrame => (Number.isInteger(f) ? f & INPUT_MASK : 0);

export const isDown = (f: InputFrame, b: number): boolean => (f & b) !== 0;
/** Pressed this tick (down now, up last tick). */
export const isPressed = (prev: InputFrame, curr: InputFrame, b: number): boolean =>
  (curr & b) !== 0 && (prev & b) === 0;
/** Released this tick. */
export const isReleased = (prev: InputFrame, curr: InputFrame, b: number): boolean =>
  (curr & b) === 0 && (prev & b) !== 0;

/**
 * Horizontal axis: -1 (L), +1 (R), 0 (none or both — SOCD "neutral" resolution so L+R never favours a side).
 */
export const horizontal = (f: InputFrame): -1 | 0 | 1 => {
  const l = f & Btn.L;
  const r = f & Btn.R;
  return l && !r ? -1 : r && !l ? 1 : 0;
};

/** Swap L and R (mirror an input, e.g. for the AI planning in a canonical attack direction). */
export const mirrorInput = (f: InputFrame): InputFrame => {
  const l = (f & Btn.L) !== 0;
  const r = (f & Btn.R) !== 0;
  return (f & ~(Btn.L | Btn.R)) | (l ? Btn.R : 0) | (r ? Btn.L : 0);
};

export interface InputBits {
  l?: boolean;
  r?: boolean;
  u?: boolean;
  d?: boolean;
  a?: boolean;
  s?: boolean;
}

/** Build a frame from booleans (devices, tests). */
export const makeInput = (b: InputBits): InputFrame =>
  (b.l ? Btn.L : 0) |
  (b.r ? Btn.R : 0) |
  (b.u ? Btn.U : 0) |
  (b.d ? Btn.D : 0) |
  (b.a ? Btn.A : 0) |
  (b.s ? Btn.S : 0);

/** Build a frame from a compact string like "R+A" or "LU" (tests / scripted replays). */
export const parseInput = (s: string): InputFrame => {
  let f = 0;
  for (const ch of s.toUpperCase()) {
    const i = BUTTON_NAMES.indexOf(ch as ButtonName);
    if (i >= 0) f |= 1 << i;
  }
  return f;
};

/** Debug string, e.g. "R·A" or "·" for none. */
export const formatInput = (f: InputFrame): string => {
  let s = '';
  for (let i = 0; i < BUTTON_NAMES.length; i++) if (f & (1 << i)) s += (s ? '·' : '') + BUTTON_NAMES[i];
  return s || '·';
};
